import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import styles from './Field.module.css';

interface FieldShellProps {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly required?: boolean;
  /** Testo per screen reader accanto all'asterisco. Tradotto dal chiamante. */
  readonly requiredLabel?: string;
  readonly children: (ids: {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

/**
 * Involucro comune a tutti i campi.
 *
 * Garanzie:
 *  · `<label for>` sempre presente e collegato (nessun placeholder al posto
 *    dell'etichetta: sparisce alla digitazione — WCAG 3.3.2)
 *  · `aria-describedby` collega suggerimento ED errore, in quest'ordine
 *  · `aria-invalid` sul controllo quando c'è errore
 *  · l'errore ha `role="alert"`: viene annunciato appena appare
 *  · "obbligatorio" è testo per screen reader, non solo un asterisco rosso
 */
function FieldShell({
  label,
  hint,
  error,
  required = false,
  requiredLabel = 'obbligatorio',
  children,
}: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
        {required ? (
          <>
            <span className={styles.required} aria-hidden="true">
              *
            </span>
            <span className="ic-sr-only"> ({requiredLabel})</span>
          </>
        ) : null}
      </label>

      {hint ? (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      ) : null}

      {children({ id, describedBy, invalid: Boolean(error) })}

      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          <span className={styles.errorIcon} aria-hidden="true">
            ⚠
          </span>
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-describedby'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly requiredLabel?: string;
}

export function TextField({
  label,
  hint,
  error,
  required,
  requiredLabel,
  className,
  ...rest
}: TextFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={required}
      {...(requiredLabel ? { requiredLabel } : {})}
    >
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          className={[styles.control, className].filter(Boolean).join(' ')}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'aria-describedby'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly requiredLabel?: string;
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  requiredLabel,
  className,
  ...rest
}: TextAreaFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={required}
      {...(requiredLabel ? { requiredLabel } : {})}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          className={[styles.control, styles.textarea, className]
            .filter(Boolean)
            .join(' ')}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'aria-describedby'> {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly hint?: string;
  readonly error?: string;
  readonly requiredLabel?: string;
  /** Prima voce neutra. Serve a non preselezionare per caso. */
  readonly placeholder?: string;
}

export function SelectField({
  label,
  options,
  hint,
  error,
  required,
  requiredLabel,
  placeholder,
  className,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={required}
      {...(requiredLabel ? { requiredLabel } : {})}
    >
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          className={[styles.control, className].filter(Boolean).join(' ')}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          {...rest}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

// ---------------------------------------------------------------------------

export interface FieldsetProps {
  readonly legend: string;
  readonly children: ReactNode;
  readonly className?: string;
}

/** Gruppo di campi correlati. `<fieldset>`+`<legend>`, non un div con un h3. */
export function Fieldset({ legend, children, className }: FieldsetProps) {
  return (
    <fieldset className={[styles.fieldset, className].filter(Boolean).join(' ')}>
      <legend className={styles.legend}>{legend}</legend>
      {children}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------

export interface HoneypotProps {
  readonly name?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * Campo trappola per i bot (vincolo 6: anti-bot SENZA captcha).
 *
 * Il captcha è un ostacolo di accessibilità dichiarato; la scelta del progetto
 * è honeypot + tempo minimo di compilazione. Va preservata.
 *
 * Perché è fatto così:
 *  · resta nel DOM e nel form (i bot che leggono l'HTML lo compilano)
 *  · è fuori dal viewport, non `display:none` (alcuni bot ignorano gli hidden)
 *  · `aria-hidden` + `tabIndex={-1}` lo tolgono dall'albero di accessibilità:
 *    chi usa uno screen reader o la tastiera non lo incontra MAI
 *  · `autoComplete="off"` evita che il browser lo compili da sé, che
 *    produrrebbe falsi positivi
 */
export function Honeypot({ name = 'ic_verifica', value, onChange }: HoneypotProps) {
  return (
    <div className={styles.honeypot} aria-hidden="true">
      <label htmlFor={`${name}-hp`}>Non compilare questo campo</label>
      <input
        id={`${name}-hp`}
        type="text"
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface ErrorSummaryProps {
  readonly title: string;
  readonly errors: readonly { readonly fieldId: string; readonly message: string }[];
}

/**
 * Sommario degli errori in testa al form.
 *
 * Pattern raccomandato per WCAG 3.3.1: al submit fallito il focus va su un
 * riepilogo con i link ai campi in errore. Senza di esso, su un form lungo
 * chi usa uno screen reader non sa quanti errori ci sono né dove sono.
 *
 * `tabIndex={-1}` serve al focus programmatico; `role="alert"` all'annuncio.
 */
export function ErrorSummary({ title, errors }: ErrorSummaryProps) {
  if (errors.length === 0) return null;
  return (
    <div className={styles.errorSummary} role="alert" tabIndex={-1}>
      <h2 className={styles.errorSummaryTitle}>{title}</h2>
      <ul className={styles.errorSummaryList}>
        {errors.map((error) => (
          <li key={error.fieldId}>
            <a href={`#${error.fieldId}`}>{error.message}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}
